import {Action} from 'redux';
import {loadDetector} from '../../../utils/detector';
import {track} from '../../../utils/tracker';
import {videoConstraints} from '../video-settings/video-constraints';
import {safelyLoadTwilio} from './load-twilio';
import {
    STATUS_CONNECTION_FAILED,
    STATUS_NO_ACCESS,
    STATUS_NO_ACCESS_CAM,
    STATUS_NO_ACCESS_MIC,
} from './constants';

export interface IRoomSettings {
    roomName: string;
    token: string;
    audioInputDeviceId: string;
    videoInputDeviceId: string;
    videoQualityId: string;
}

export interface IVideoAction extends Action {
    errorName?: string;
    payload?: any;
    meta?: any;
    status?: string;
    shouldCleanup?: boolean;
}

const {LocalVideoTrack, connect, createLocalTracks} = safelyLoadTwilio();

export const VIDEO_CONNECT                   = 'VIDEO_CONNECT';
export const VIDEO_CONNECT_PENDING           = 'VIDEO_CONNECT_PENDING';
export const VIDEO_CONNECT_FULFILLED         = 'VIDEO_CONNECT_FULFILLED';
export const VIDEO_CONNECT_REJECTED          = 'VIDEO_CONNECT_REJECTED';
export const VIDEO_DISCONNECT                = 'VIDEO_DISCONNECT';
export const VIDEO_SEND_SCREEN               = 'VIDEO_SEND_SCREEN';
export const VIDEO_SEND_SCREEN_STOP          = 'VIDEO_SEND_SCREEN_STOP';
export const VIDEO_SEND_SCREEN_ERROR         = 'VIDEO_SEND_SCREEN_ERROR';
export const VIDEO_SEND_SCREEN_ERROR_CONFIRM = 'VIDEO_SEND_SCREEN_ERROR_CONFIRM';
export const VIDEO_UPDATE_STATUS             = 'VIDEO_UPDATE_STATUS';

const SendScreenNotSupportedError = 'SendScreenNotSupportedError';

export const connectToRoom = roomSettings => async dispatch => {
    const {
        roomName,
        token,
        audioInputDeviceId,
        videoInputDeviceId,
        videoQualityId,
    } = roomSettings;

    // Blocks until user allows or blocks video.
    const videoTracks = await createLocalTracks({
        audio: {deviceId: audioInputDeviceId},
        video: videoConstraints(videoInputDeviceId, videoQualityId),
    }).catch(() => undefined);

    if (!videoTracks) {
        track('video blocked');
        dispatch({type: VIDEO_UPDATE_STATUS, status: await getMediaStatus()});
        return;
    }
    track('video allowed');

    // Try to reconnect when disconnected.
    const options = {insights: true, name: roomName, tracks: videoTracks};
    const disconnect = () => dispatch({type: VIDEO_DISCONNECT});
    const reconnect = () => dispatch({type: VIDEO_CONNECT, payload: connect(token, options)});
    window.addEventListener('beforeunload', disconnect);
    window.addEventListener('offline', disconnect);
    window.addEventListener('online', reconnect);

    await dispatch({
        type: VIDEO_CONNECT,
        payload: connect(token, options),
        meta: {cleanup: () => {
            window.removeEventListener('beforeunload', disconnect);
            window.removeEventListener('offline', disconnect);
            window.removeEventListener('online', reconnect);
        }},
    }).catch((err) => {
        dispatch({type: VIDEO_UPDATE_STATUS, status: STATUS_CONNECTION_FAILED});
    });
};

export const disconnectFromRoom = () => ({type: VIDEO_DISCONNECT, shouldCleanup: true});

export const sendScreen = (room, interviewType, startTime) => async dispatch => {
    let errorName;

    // Blocks until user allows or blocks screen sharing.
    const stream = await getUserScreen().catch(e => {
        errorName = e.name;
        dispatch({type: VIDEO_SEND_SCREEN_ERROR, errorName});
    });

    const minutesFromStart = Math.floor((Date.now() - startTime.getTime()) / (1000 * 60));
    const interviewTypeApiName = interviewType.apiName;
    if (!stream) {
        track('screen sending failed', {
            errorName,
            interviewType: interviewTypeApiName,
            minutesFromStart,
        });
        return;
    }
    track('screen sending succeeded', {
        interviewType: interviewTypeApiName,
        minutesFromStart,
    });

    const screenTrack = new LocalVideoTrack(stream.getVideoTracks()[0], {name: 'screen'});
    screenTrack.once('stopped', () => {
        room.localParticipant.unpublishTrack(screenTrack);
        dispatch({type: VIDEO_SEND_SCREEN_STOP});
    });
    room.localParticipant.publishTrack(screenTrack);

    dispatch({type: VIDEO_SEND_SCREEN});
};

export const confirmSendScreenError = () => ({type: VIDEO_SEND_SCREEN_ERROR_CONFIRM});

const getMediaStatus = async () => {
    const {
        isWebsiteHasMicrophonePermissions: hasMic,
        isWebsiteHasWebcamPermissions: hasCam,
    } = await loadDetector();

    if (hasMic && !hasCam) {
        return STATUS_NO_ACCESS_CAM;
    }
    if (hasCam && !hasMic) {
        return STATUS_NO_ACCESS_MIC;
    }
    return STATUS_NO_ACCESS;
};

const getUserScreen = async (): Promise<MediaStream> => {
    const mediaDevices = navigator.mediaDevices as any;

    // Works in Chrome 72, Edge 17, and Firefox 66.
    if (mediaDevices.getDisplayMedia) {
        return mediaDevices.getDisplayMedia();
    }

    // Works in older versions of Firefox.
    if (mediaDevices.getSupportedConstraints().mediaSource) {
        return mediaDevices.getUserMedia({video: {mediaSource: 'screen'}});
    }

    const error = new Error('Unable to send screen');
    error.name = SendScreenNotSupportedError;
    throw error;
};
